/**
 * Tearsheets domain contracts.
 *
 * FE renders HTML/PDF; BFF stores layouts, skins, and branding components.
 * SOAP supplies live data / funds — never rendering.
 */
import { Type, type Static } from '@sinclair/typebox';
import { IsoDate, IsoDateTime, SectionSpan, TextAlign, YearMonth } from './common';
import { StringEnum, createSchema } from '../../util/schema';

// ─── Domain enums ─────────────────────────────────────────────────────────────

export const TearsheetReportStatus = StringEnum(['draft', 'submitted', 'approved', 'published', 'archived'], {
  description: 'Publish workflow states for a saved tearsheet report. Reject moves to archived.',
});
export type TearsheetReportStatus = Static<typeof TearsheetReportStatus>;

export const TearsheetTermFrequency = StringEnum(['monthly', 'quarterly']);
export type TearsheetTermFrequency = Static<typeof TearsheetTermFrequency>;

/** Canonical movable section ids. Custom boxes use custom_*. Catalog v1 adds HFA-backed tables/charts. */
export const TEARSHEET_SECTION_IDS = [
  'header',
  'kpis',
  'commentary',
  'monthly',
  'vami',
  'risk',
  'sector_attribution',
  'exposure_attribution',
  'last_month_attribution',
  'top_exposure',
  'bottom_exposure',
  'top_gross_performance',
  'bottom_gross_performance',
  'volatility_contribution',
  'volatility_instruments',
  'drawdowns',
  'contact',
  'subscription',
  'disclaimer',
  'portfolio_manager',
  'predefined_text',
  // Tearsheet Item Catalog v1 (additive; keep existing MVP ids)
  'rolling_summary',
  'largest_drawdowns',
  'annual_returns',
  'correlation_to_indices',
  'tier_item_properties',
  'cumulative_return',
  'sector_risk_alloc_var',
  'initial_margin_equity',
  'maintenance_margin_equity',
  'monthly_return_and_drawdown',
  'rolling_return',
  'rolling_volatility',
  'monthly_returns_distribution',
  'monthly_returns_bar',
  'monthly_drawdown_bar',
  'vami_and_monthly_returns',
  'rolling_sharpe',
  'rolling_volatility_annualized',
  'component_var_by_sector',
  'performance_during_index_worst',
  'sector_decomposition',
  'risk_statistics',
  'return_statistics',
] as const;

/**
 * Builder "Add section" allowlist. Excludes report chrome (`header` / `kpis` / `contact` / `disclaimer`).
 */
export const TEARSHEET_ADDABLE_SECTION_IDS = [
  'commentary',
  'monthly',
  'vami',
  'risk',
  'sector_attribution',
  'exposure_attribution',
  'last_month_attribution',
  'top_exposure',
  'bottom_exposure',
  'top_gross_performance',
  'bottom_gross_performance',
  'volatility_contribution',
  'volatility_instruments',
  'drawdowns',
  'subscription',
  'portfolio_manager',
  'predefined_text',
  'rolling_summary',
  'largest_drawdowns',
  'annual_returns',
  'correlation_to_indices',
  'tier_item_properties',
  'cumulative_return',
  'sector_risk_alloc_var',
  'initial_margin_equity',
  'maintenance_margin_equity',
  'monthly_return_and_drawdown',
  'rolling_return',
  'rolling_volatility',
  'monthly_returns_distribution',
  'monthly_returns_bar',
  'monthly_drawdown_bar',
  'vami_and_monthly_returns',
  'rolling_sharpe',
  'rolling_volatility_annualized',
  'component_var_by_sector',
  'performance_during_index_worst',
  'sector_decomposition',
  'risk_statistics',
  'return_statistics',
] as const;

// ─── Layout components (per-datasource branding blocks) ───────────────────────

export const TearsheetLayoutComponentType = StringEnum(
  [
    'logo',
    'advisor',
    'subtitle',
    'disclaimer',
    'portfolio_manager',
    'benchmarks',
    'typography',
    'program_description',
    'header_text',
    'subscription',
  ],
  {
    description: 'Component types stored in TearsheetDatasourceConfiguration (branding presets or per-fund custom fields).',
  },
);
export type TearsheetLayoutComponentType = Static<typeof TearsheetLayoutComponentType>;

/** Datasource-wide branding rows (`ds#{datasource}#{type}`). */
export const BRANDING_LAYOUT_COMPONENT_TYPES = [
  'logo',
  'advisor',
  'subtitle',
  'disclaimer',
  'portfolio_manager',
  'benchmarks',
  'typography',
] as const;

/** Per fund + as-of copy (`ds#{datasource}#fund#{fundId}#asOf#{YYYY-MM}#{type}`). */
export const FUND_COPY_LAYOUT_COMPONENT_TYPES = ['program_description', 'header_text', 'subscription'] as const;
export type FundCopyLayoutComponentType = (typeof FUND_COPY_LAYOUT_COMPONENT_TYPES)[number];

/** Types allowed in datasource configuration — no other layout chrome is writable here. */
export const EDITABLE_LAYOUT_COMPONENT_TYPES = [...BRANDING_LAYOUT_COMPONENT_TYPES, ...FUND_COPY_LAYOUT_COMPONENT_TYPES] as const;

/** Max HFA index rows a datasource can pin for correlation / worst-month charts. */
export const TEARSHEET_BENCHMARK_MAX_ITEMS = 12;

/** HFA `getFilteredInstruments` throws above this many rows — typeahead must stay at or under it. */
export const TEARSHEET_INDEX_SEARCH_MAX_ITEMS = 200;

/** Minimum `q` length before HFA index search (avoids the unfiltered "too many instruments" fault). */
export const TEARSHEET_INDEX_SEARCH_MIN_LENGTH = 2;

/** Maximum `q` length forwarded to HFA `CommodityDescription`. */
export const TEARSHEET_INDEX_SEARCH_MAX_LENGTH = 80;

/** Decoded binary size limit for layout-component image uploads (logo / portfolio photo). */
export const TEARSHEET_LAYOUT_ASSET_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Base64 string length gate for asset PUT bodies (~4/3 of {@link TEARSHEET_LAYOUT_ASSET_MAX_BYTES}, with headroom).
 * Rejects oversized payloads before decode.
 */
export const TEARSHEET_LAYOUT_ASSET_MAX_BASE64_LENGTH = 3 * 1024 * 1024;

/** Allowed `mimeType` values for layout-component image uploads. */
export const TEARSHEET_LAYOUT_ASSET_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;
export type TearsheetLayoutAssetMimeType = (typeof TEARSHEET_LAYOUT_ASSET_MIME_TYPES)[number];

/** Presigned GET lifetime for branding images in the builder (seconds). */
export const TEARSHEET_LAYOUT_ASSET_PRESIGN_SECONDS = 30;

/** Presigned GET lifetime for generated tearsheet PDFs (seconds). */
export const TEARSHEET_PDF_PRESIGN_SECONDS = 120;

/** Character cap for branding rich text (disclaimer, portfolio bio) — stored HF markup length. */
export const TEARSHEET_RICH_TEXT_MAX_LENGTH = 4000;

/** Character cap for short branding text fields (advisor, subtitle, name, role, contact). */
export const TEARSHEET_LAYOUT_TEXT_MAX_LENGTH = 320;

/** Minimal rich text: bold, italic, underline, paragraphs. Stored as HF markup (PUA delimiters) — never HTML. */
export const TearsheetRichText = Type.String({
  maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH,
  description: 'HF rich text (PUA delimiters for bold/italic/underline; paragraphs separated by blank lines). No HTML.',
});
export type TearsheetRichText = Static<typeof TearsheetRichText>;

/** Short branding text (names, subtitle, role, contact info). */
export const TearsheetLayoutText = Type.String({
  maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH,
});
export type TearsheetLayoutText = Static<typeof TearsheetLayoutText>;

const layoutComponentBase = {
  componentId: Type.String({ minLength: 1 }),
  datasource: Type.String({ minLength: 1 }),
  /** Always true — Dynamo rows in this table are the only editable branding components. */
  editable: Type.Literal(true),
  /** Hashed HedgeFacts user id (`h1.…`). */
  updatedBy: Type.String({ minLength: 1 }),
  updatedAt: IsoDateTime,
};

export const TearsheetLayoutComponentLogoPayload = Type.Object(
  {
    s3Key: Type.Optional(Type.String({ minLength: 1 })),
    contentType: Type.Optional(Type.String({ minLength: 1 })),
    assetUrl: Type.Optional(Type.String()),
  },
  { $id: 'TearsheetLayoutComponentLogoPayload' },
);
export type TearsheetLayoutComponentLogoPayload = Static<typeof TearsheetLayoutComponentLogoPayload>;

export const TearsheetLayoutComponentAdvisorPayload = Type.Object(
  {
    name: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '' })),
  },
  { $id: 'TearsheetLayoutComponentAdvisorPayload' },
);
export type TearsheetLayoutComponentAdvisorPayload = Static<typeof TearsheetLayoutComponentAdvisorPayload>;

export const TearsheetLayoutComponentSubtitlePayload = Type.Object(
  {
    text: Type.Optional(
      Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '', description: 'Header subtitle (e.g. Monthly Tearsheet).' }),
    ),
  },
  { $id: 'TearsheetLayoutComponentSubtitlePayload' },
);
export type TearsheetLayoutComponentSubtitlePayload = Static<typeof TearsheetLayoutComponentSubtitlePayload>;

export const TearsheetDisclaimerPeriod = Type.Object(
  {
    richText: Type.Optional(
      Type.String({
        maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH,
        description: 'HF rich text effective from this month until a later period.',
        default: '',
      }),
    ),
    frozen: Type.Optional(Type.Boolean({ description: 'When true, an earlier month does not overwrite this period or any month after it.' })),
  },
  { $id: 'TearsheetDisclaimerPeriod' },
);
export type TearsheetDisclaimerPeriod = Static<typeof TearsheetDisclaimerPeriod>;

export const TearsheetLayoutComponentDisclaimerPayload = Type.Object(
  {
    richText: Type.Optional(
      Type.String({
        maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH,
        description: 'Baseline HF rich text for months before the first period entry. No HTML.',
        default: '',
      }),
    ),
    /** Set on a month-scoped read: this month is frozen. */
    frozen: Type.Optional(Type.Boolean()),
    periods: Type.Optional(
      Type.Record(Type.String({ pattern: '^\\d{4}-\\d{2}$' }), TearsheetDisclaimerPeriod, {
        description:
          'Month (`YYYY-MM`) → disclaimer. A month with no entry inherits the latest earlier entry, frozen or not. Saving an earlier month copies forward until the first later frozen month.',
      }),
    ),
  },
  { $id: 'TearsheetLayoutComponentDisclaimerPayload' },
);
export type TearsheetLayoutComponentDisclaimerPayload = Static<typeof TearsheetLayoutComponentDisclaimerPayload>;

export const TearsheetLayoutComponentPortfolioManagerPayload = Type.Object(
  {
    name: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '' })),
    role: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '' })),
    contactInfo: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '' })),
    richText: Type.Optional(
      Type.String({
        maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH,
        description: 'HF rich text (PUA delimiters for bold/italic/underline; paragraphs separated by blank lines). No HTML.',
        default: '',
      }),
    ),
    photoS3Key: Type.Optional(Type.String({ minLength: 1 })),
    photoContentType: Type.Optional(Type.String({ minLength: 1 })),
    photoAssetUrl: Type.Optional(Type.String()),
  },
  { $id: 'TearsheetLayoutComponentPortfolioManagerPayload' },
);
export type TearsheetLayoutComponentPortfolioManagerPayload = Static<typeof TearsheetLayoutComponentPortfolioManagerPayload>;

export const TearsheetBenchmarkItem = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 32, description: 'HFA index instrument id (Context=INDEX).' }),
    name: Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '', description: 'Display name (e.g. S&P 500 INDEX).' }),
  },
  { $id: 'TearsheetBenchmarkItem' },
);
export type TearsheetBenchmarkItem = Static<typeof TearsheetBenchmarkItem>;

export const TearsheetLayoutComponentBenchmarksPayload = Type.Object(
  {
    items: Type.Array(TearsheetBenchmarkItem, { maxItems: TEARSHEET_BENCHMARK_MAX_ITEMS, default: [] }),
  },
  { $id: 'TearsheetLayoutComponentBenchmarksPayload' },
);
export type TearsheetLayoutComponentBenchmarksPayload = Static<typeof TearsheetLayoutComponentBenchmarksPayload>;

/** Web-safe family choices for the tearsheet. The renderer maps each id to a CSS font stack. */
export const TearsheetFontFamily = StringEnum(['arial', 'georgia', 'verdana']);
export type TearsheetFontFamily = Static<typeof TearsheetFontFamily>;

export const TearsheetFontSize = StringEnum(['small', 'medium', 'large']);
export type TearsheetFontSize = Static<typeof TearsheetFontSize>;

export const TearsheetLayoutComponentTypographyPayload = Type.Object(
  {
    fontFamily: Type.Optional(TearsheetFontFamily),
    fontSize: Type.Optional(TearsheetFontSize),
    bold: Type.Optional(Type.Boolean({ default: false })),
  },
  {
    $id: 'TearsheetLayoutComponentTypographyPayload',
    description: 'Datasource-wide font defaults; a box may override them via reportConfig.sectionStyles.',
  },
);
export type TearsheetLayoutComponentTypographyPayload = Static<typeof TearsheetLayoutComponentTypographyPayload>;

export const TearsheetIndexQuery = Type.Object(
  {
    q: Type.String({
      minLength: TEARSHEET_INDEX_SEARCH_MIN_LENGTH,
      maxLength: TEARSHEET_INDEX_SEARCH_MAX_LENGTH,
      description: 'Commodity description search (HFA LIKE). Append % on the server if missing.',
    }),
  },
  { $id: 'TearsheetIndexQuery' },
);
export type TearsheetIndexQuery = Static<typeof TearsheetIndexQuery>;
export const tearsheetIndexQuerySchema = createSchema(TearsheetIndexQuery);

export const TearsheetIndexList = Type.Object(
  {
    indexes: Type.Array(TearsheetBenchmarkItem, { maxItems: TEARSHEET_INDEX_SEARCH_MAX_ITEMS, default: [] }),
    warning: Type.Optional(Type.String({ description: 'Set when HFA refuses the search (too many matches).' })),
  },
  { $id: 'TearsheetIndexList' },
);
export type TearsheetIndexList = Static<typeof TearsheetIndexList>;
export const tearsheetIndexListSchema = createSchema(TearsheetIndexList);

export const TearsheetLayoutComponentCopyPayload = Type.Object(
  {
    title: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH, default: '' })),
    text: Type.Optional(Type.String({ maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH, default: '' })),
    frozen: Type.Optional(Type.Boolean({ description: 'When true, an earlier month does not overwrite this period or any month after it.' })),
  },
  { $id: 'TearsheetLayoutComponentCopyPayload' },
);
export type TearsheetLayoutComponentCopyPayload = Static<typeof TearsheetLayoutComponentCopyPayload>;

/** Per-fund copy fields keyed by period (`all` or `YYYY-MM`) then field id (`program_description`, `header_text`). */
export const TearsheetFundCopyFieldMap = Type.Record(Type.String(), TearsheetLayoutComponentCopyPayload, {
  description: 'Fund-scoped copy fields for one period bucket.',
});
export type TearsheetFundCopyFieldMap = Static<typeof TearsheetFundCopyFieldMap>;

export const TearsheetFundCustomFieldPeriods = Type.Record(Type.String({ pattern: '^(?:all|\\d{4}-\\d{2})$' }), TearsheetFundCopyFieldMap, {
  description:
    'Period bucket: `all` or `YYYY-MM`. A month with no entry inherits the latest earlier month, frozen or not. Saving an earlier month copies forward until the first later frozen month.',
});
export type TearsheetFundCustomFieldPeriods = Static<typeof TearsheetFundCustomFieldPeriods>;

export const TearsheetCustomFields = Type.Record(Type.String(), TearsheetFundCustomFieldPeriods, {
  description: 'Fund id → period → field id → copy payload.',
});
export type TearsheetCustomFields = Static<typeof TearsheetCustomFields>;

export const TearsheetBrandingPresets = Type.Object(
  {
    logo: Type.Optional(TearsheetLayoutComponentLogoPayload),
    advisor: Type.Optional(TearsheetLayoutComponentAdvisorPayload),
    subtitle: Type.Optional(TearsheetLayoutComponentSubtitlePayload),
    portfolio_manager: Type.Optional(TearsheetLayoutComponentPortfolioManagerPayload),
    disclaimer: Type.Optional(TearsheetLayoutComponentDisclaimerPayload),
    benchmarks: Type.Optional(TearsheetLayoutComponentBenchmarksPayload),
    typography: Type.Optional(TearsheetLayoutComponentTypographyPayload),
  },
  { $id: 'TearsheetBrandingPresets', description: 'Datasource-wide branding defaults.' },
);
export type TearsheetBrandingPresets = Static<typeof TearsheetBrandingPresets>;

const fundCopyBase = {
  ...layoutComponentBase,
  fundId: Type.String({ minLength: 1, description: 'Hashed fund id from the builder URL.' }),
  asOf: YearMonth,
};

export const TearsheetLayoutComponent = Type.Union(
  [
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('logo'),
      payload: TearsheetLayoutComponentLogoPayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('advisor'),
      payload: TearsheetLayoutComponentAdvisorPayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('subtitle'),
      payload: TearsheetLayoutComponentSubtitlePayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('disclaimer'),
      payload: TearsheetLayoutComponentDisclaimerPayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('portfolio_manager'),
      payload: TearsheetLayoutComponentPortfolioManagerPayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('benchmarks'),
      payload: TearsheetLayoutComponentBenchmarksPayload,
    }),
    Type.Object({
      ...layoutComponentBase,
      type: Type.Literal('typography'),
      payload: TearsheetLayoutComponentTypographyPayload,
    }),
    Type.Object({
      ...fundCopyBase,
      type: Type.Literal('program_description'),
      payload: TearsheetLayoutComponentCopyPayload,
    }),
    Type.Object({
      ...fundCopyBase,
      type: Type.Literal('header_text'),
      payload: TearsheetLayoutComponentCopyPayload,
    }),
    Type.Object({
      ...fundCopyBase,
      type: Type.Literal('subscription'),
      payload: TearsheetLayoutComponentCopyPayload,
    }),
  ],
  { $id: 'TearsheetLayoutComponent' },
);
export type TearsheetLayoutComponent = Static<typeof TearsheetLayoutComponent>;
export const tearsheetLayoutComponentSchema = createSchema(TearsheetLayoutComponent);

export const TearsheetLayoutComponentList = Type.Object(
  {
    components: Type.Array(TearsheetLayoutComponent),
  },
  { $id: 'TearsheetLayoutComponentList' },
);
export type TearsheetLayoutComponentList = Static<typeof TearsheetLayoutComponentList>;
export const tearsheetLayoutComponentListSchema = createSchema(TearsheetLayoutComponentList);

export const TearsheetLayoutComponentInput = Type.Object(
  {
    type: TearsheetLayoutComponentType,
    name: Type.Optional(TearsheetLayoutText),
    subtitle: Type.Optional(TearsheetLayoutText),
    role: Type.Optional(TearsheetLayoutText),
    contactInfo: Type.Optional(TearsheetLayoutText),
    richText: Type.Optional(TearsheetRichText),
    title: Type.Optional(TearsheetLayoutText),
    text: Type.Optional(Type.String({ maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH })),
    items: Type.Optional(Type.Array(TearsheetBenchmarkItem, { maxItems: TEARSHEET_BENCHMARK_MAX_ITEMS })),
    fontFamily: Type.Optional(TearsheetFontFamily),
    fontSize: Type.Optional(TearsheetFontSize),
    bold: Type.Optional(Type.Boolean()),
    fundId: Type.Optional(Type.String({ minLength: 1 })),
    asOf: Type.Optional(YearMonth),
    frozen: Type.Optional(Type.Boolean({ description: 'Keep this month when an earlier month is edited. Fund copy and disclaimer only.' })),
  },
  { $id: 'TearsheetLayoutComponentInput', description: 'Text fields for PUT /tearsheets/layout-components/{componentType}.' },
);
export type TearsheetLayoutComponentInput = Static<typeof TearsheetLayoutComponentInput>;
export const tearsheetLayoutComponentInputSchema = createSchema(TearsheetLayoutComponentInput);

export const TearsheetLayoutComponentAssetInput = Type.Object(
  {
    filename: Type.String({ minLength: 1 }),
    mimeType: Type.String({
      minLength: 1,
      description: `One of: ${TEARSHEET_LAYOUT_ASSET_MIME_TYPES.join(', ')}. Filename extension may be used as a fallback when empty/unknown.`,
    }),
    contentBase64: Type.String({
      minLength: 1,
      maxLength: TEARSHEET_LAYOUT_ASSET_MAX_BASE64_LENGTH,
      description: `Base64-encoded image bytes. Decoded size must be ≤ ${TEARSHEET_LAYOUT_ASSET_MAX_BYTES} bytes.`,
    }),
  },
  { $id: 'TearsheetLayoutComponentAssetInput' },
);
export type TearsheetLayoutComponentAssetInput = Static<typeof TearsheetLayoutComponentAssetInput>;
export const tearsheetLayoutComponentAssetInputSchema = createSchema(TearsheetLayoutComponentAssetInput);

export const TearsheetLayoutComponentTypeParam = Type.Object(
  {
    componentType: TearsheetLayoutComponentType,
  },
  { $id: 'TearsheetLayoutComponentTypeParam' },
);
export type TearsheetLayoutComponentTypeParam = Static<typeof TearsheetLayoutComponentTypeParam>;
export const tearsheetLayoutComponentTypeParamSchema = createSchema(TearsheetLayoutComponentTypeParam);

export const TearsheetLayoutComponentListQuery = Type.Object(
  {
    fundId: Type.Optional(Type.String({ minLength: 1 })),
    reportDate: Type.Optional(YearMonth),
    /** Partner view/PDF: resolve the PartnerTearsheets snapshot logo for this HF report. */
    reportId: Type.Optional(Type.String({ minLength: 1 })),
    /** When set, return only this layout component row. */
    type: Type.Optional(TearsheetLayoutComponentType),
  },
  { $id: 'TearsheetLayoutComponentListQuery', description: 'When both are set, list also returns that fund/as-of copy rows.' },
);
export type TearsheetLayoutComponentListQuery = Static<typeof TearsheetLayoutComponentListQuery>;
export const tearsheetLayoutComponentListQuerySchema = createSchema(TearsheetLayoutComponentListQuery);

/** Domain `listLayoutComponents` invoke body (BFF → tearsheets). */
export const TearsheetLayoutComponentListRequest = Type.Object(
  {
    datasource: Type.String({ minLength: 1 }),
    userId: Type.Integer(),
    fundId: Type.Optional(Type.String({ minLength: 1 })),
    asOf: Type.Optional(YearMonth),
    type: Type.Optional(TearsheetLayoutComponentType),
  },
  { $id: 'TearsheetLayoutComponentListRequest' },
);
export type TearsheetLayoutComponentListRequest = Static<typeof TearsheetLayoutComponentListRequest>;
export const tearsheetLayoutComponentListRequestSchema = createSchema(TearsheetLayoutComponentListRequest);

// ─── Row / section shapes (FE render inputs) ──────────────────────────────────

export const MonthlyReturnRow = Type.Object({
  year: Type.Integer(),
  jan: Type.String(),
  feb: Type.String(),
  mar: Type.String(),
  apr: Type.String(),
  may: Type.String(),
  jun: Type.String(),
  jul: Type.String(),
  aug: Type.String(),
  sep: Type.String(),
  oct: Type.String(),
  nov: Type.String(),
  dec: Type.String(),
  ytd: Type.String(),
});
export type MonthlyReturnRow = Static<typeof MonthlyReturnRow>;

export const VamiPoint = Type.Object({
  date: IsoDate,
  series: Type.Record(Type.String(), Type.Number()),
});
export type VamiPoint = Static<typeof VamiPoint>;

export const RiskSummaryRow = Type.Object({
  statistic: Type.String(),
  values: Type.Record(Type.String(), Type.Union([Type.String(), Type.Number()])),
});
export type RiskSummaryRow = Static<typeof RiskSummaryRow>;

export const SectorAttributionRow = Type.Object({
  month: Type.String(),
  sectors: Type.Record(Type.String(), Type.Number()),
  grossReturn: Type.String(),
});
export type SectorAttributionRow = Static<typeof SectorAttributionRow>;

export const ExposureAttributionRow = Type.Object({
  assetClass: Type.String(),
  longExposure: Type.String(),
  shortExposure: Type.String(),
  longAttribution: Type.String(),
  shortAttribution: Type.String(),
});
export type ExposureAttributionRow = Static<typeof ExposureAttributionRow>;

export const InstrumentRow = Type.Object({
  symbol: Type.String(),
  name: Type.String(),
  assetClass: Type.String(),
  direction: Type.String(),
  pctNav: Type.Optional(Type.String()),
  pnl: Type.Optional(Type.String()),
  volContribution: Type.Optional(Type.String()),
});
export type InstrumentRow = Static<typeof InstrumentRow>;

export const VolatilityContribution = Type.Object({
  byAssetClass: Type.Array(
    Type.Object({
      assetClass: Type.String(),
      volContribution: Type.String(),
    }),
  ),
  topInstruments: Type.Array(InstrumentRow),
});
export type VolatilityContribution = Static<typeof VolatilityContribution>;

export const KeyValueRow = Type.Object({
  item: Type.String(),
  value: Type.String(),
});
export type KeyValueRow = Static<typeof KeyValueRow>;

export const DrawdownRow = Type.Object({
  start: Type.String(),
  trough: Type.String(),
  length: Type.String(),
  drawdown: Type.String(),
  recovery: Type.String(),
  /** Next 12-month return after trough (catalog Drawdown Table). */
  next12MonthReturn: Type.Optional(Type.String()),
});
export type DrawdownRow = Static<typeof DrawdownRow>;

export const AnnualReturnRow = Type.Object({
  year: Type.String(),
  returnPct: Type.String(),
});
export type AnnualReturnRow = Static<typeof AnnualReturnRow>;

export const RollingSummaryRow = Type.Object({
  windowMonths: Type.Integer(),
  nReturn: Type.String(),
  avgReturn: Type.String(),
  nStd: Type.String(),
  avgStdAnnualized: Type.String(),
  nSharpe: Type.String(),
  avgSharpe: Type.String(),
});
export type RollingSummaryRow = Static<typeof RollingSummaryRow>;

export const CorrelationRow = Type.Object({
  indexName: Type.String(),
  correlation: Type.String(),
});
export type CorrelationRow = Static<typeof CorrelationRow>;

/** Date series for catalog charts (cumulative, monthly+DD, rolling, margin ratios). */
export const TearsheetSeriesPoint = Type.Object({
  date: IsoDate,
  values: Type.Record(Type.String(), Type.Number()),
});
export type TearsheetSeriesPoint = Static<typeof TearsheetSeriesPoint>;

export const NamedValueRow = Type.Object({
  name: Type.String(),
  value: Type.String(),
});
export type NamedValueRow = Static<typeof NamedValueRow>;

export const IndexWorstMonthRow = Type.Object({
  date: Type.String(),
  indexReturn: Type.String(),
  fundReturn: Type.String(),
});
export type IndexWorstMonthRow = Static<typeof IndexWorstMonthRow>;

/**
 * Performance Statistics picker: catalog label → HFA `Summary` column + value type.
 * Seeded from catalog docx + HFAStatTable (FactsMaster AvailableStatistics is not in git).
 */
export const PERFORMANCE_STAT_FIELDS = [
  { fieldName: 'Reserved7', label: 'CAGR', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Reserved6', label: 'CMGR', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'GSD', label: 'GSD', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'MonthlySTDAnnualized', label: 'Annualized GSD', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'GeometricDownsideDeviation', label: 'Gmtrc. Downside Deviation', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'SharpeRatio', label: 'Sharpe Ratio', valueType: 'Ratio' as const, group: 'risk' as const },
  { fieldName: 'SortinoRatio', label: 'Sortino Ratio', valueType: 'Ratio' as const, group: 'risk' as const },
  { fieldName: 'SterlingRatio', label: 'Sterling Ratio', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'AvgGain', label: 'Average Gain', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'AvgLoss', label: 'Average Loss', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'MaximumDDPcnt', label: 'Max. Drawdown', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'Reserved1', label: 'Max. Drawdown Duration', valueType: 'Number' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'Largest6MRolling', label: 'Max. 6 Month ROR', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month1Ret', label: 'MTD', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'YTDRet', label: 'YTD', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month12Ret', label: 'Last 12 Month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'NetPrft', label: 'Total Return', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'AvgMReturn', label: 'Avg. Monthly Return', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'MonthsInMaxDD', label: 'Max. Drawdown Period', valueType: 'Months' as const, group: 'risk' as const },
  { fieldName: 'BestMonth', label: 'Best Month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'WorstMonth', label: 'Worst Month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'PcntPrftM', label: 'Profitable Months %', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'PcntLossM', label: 'Losing Months %', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'AvgAReturn', label: 'Avg. Annual Return', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Best12Month', label: 'Best 12-Month Period', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Worst12Month', label: 'Worst 12-Month Period', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'StdAReturn', label: 'StdDev Annual Return', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'StdMReturn', label: 'StdDev Monthly Return', valueType: '%' as const, group: 'risk' as const },
  { fieldName: 'SemiSharpeRatio', label: 'Semi Dev. Sharpe', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'AvgRecoveryM', label: 'Avg Months to Recovery', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'Month3Ret', label: '3-month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month24Ret', label: '24-month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month36Ret', label: '36-month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month48Ret', label: '48-month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Month60Ret', label: '60-month', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'Skewness', label: 'Skewness', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'Kurtosis', label: 'Kurtosis', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'Reserved2', label: 'Alpha', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved3', label: 'Annualized Alpha', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved4', label: 'Beta', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved5', label: 'Correlation', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'LongestBetweenEP', label: 'Longest Period Between Peaks', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'ExpNewPeackM', label: 'Avg Months to New Peak', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 2 as const },
  { fieldName: 'PcntPrftA', label: '% Years Profitable', valueType: '%' as const, group: 'return' as const },
  { fieldName: 'DownsideDeviation', label: 'Downside Deviation', valueType: '%' as const, group: 'risk' as const },
  // HFA Summary formats Reserved8–11 as ###0.000 (cDataTypes / HFAStatTable), not percents.
  { fieldName: 'Reserved8', label: 'Tracking Error', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved9', label: 'Jensen Alpha', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved10', label: 'Information Ratio', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
  { fieldName: 'Reserved11', label: 'Coefficient of Determination', valueType: 'Ratio' as const, group: 'risk' as const, decimals: 3 as const },
] as const;

export const RISK_STAT_LABELS = PERFORMANCE_STAT_FIELDS.filter((field) => field.group === 'risk').map((field) => field.label);
export const RETURN_STAT_LABELS = PERFORMANCE_STAT_FIELDS.filter((field) => field.group === 'return').map((field) => field.label);

/**
 * Live tearsheet header KPIs: HFA `Summary` columns first, dashboard MTD/YTD as fallback.
 * Seeded from catalog tearsheet KPI row + dashboard statistics field names.
 */
export const TEARSHEET_KPI_FIELDS = [
  { label: 'Cumulative Return', summaryKeys: ['NetPrft'], percent: true },
  { label: 'MTD', summaryKeys: ['Month1Ret'], dashboardKeys: ['ReturnPct_MTD', 'MTD', 'ReturnPct'], percent: true },
  { label: 'YTD', summaryKeys: ['YTDRet'], dashboardKeys: ['ReturnPct_YTD', 'YTD'], percent: true },
  { label: '12 Months', summaryKeys: ['Month12Ret'], percent: true },
  { label: 'Volatility', summaryKeys: ['MonthlySTDAnnualized'], percent: true },
  { label: 'Sharpe', summaryKeys: ['SharpeRatio'] },
  { label: 'Sortino', summaryKeys: ['SortinoRatio'] },
  { label: 'CAGR', summaryKeys: ['Reserved7'], percent: true },
  { label: 'Max. Drawdown', summaryKeys: ['MaximumDDPcnt'], percent: true },
] as const;

/** HFA `CustomData` → `subscriptionInformation` rows (catalog terms table). */
export const TEARSHEET_SUBSCRIPTION_TERM_KEYS = [
  { keys: ['Liquidity', 'LIQUIDITY'], item: 'Liquidity' },
  { keys: ['FeeStructure', 'FEE_STRUCTURE'], item: 'Fee structure' },
  { keys: ['MinimumInvestment', 'MinInvestment', 'MINIMUM_INVESTMENT'], item: 'Minimum investment' },
  { keys: ['LockupPeriod', 'Lockup', 'LOCK_UP_PERIOD', 'LOCKUP'], item: 'Lockup' },
  { keys: ['Legal', 'LEGAL'], item: 'Legal' },
  { keys: ['Administrator', 'ADMINISTRATOR'], item: 'Administrator' },
  { keys: ['MgmFee', 'TYPICAL_MANAGEMENT_FEE'], item: 'Typical management fee' },
  { keys: ['PerfFee', 'TYPICAL_PERFORMANCE_FEE'], item: 'Typical performance fee' },
  { keys: ['Subscriptions', 'SUBSCRIPTIONS'], item: 'Subscriptions' },
  { keys: ['Redemptions', 'REDEMPTIONS'], item: 'Redemptions' },
  { keys: ['TYPE', 'Type'], item: 'Type' },
  { keys: ['ADVANCE_NOTICE', 'AdvanceNotice'], item: 'Advance notice' },
] as const;

export const TearsheetHeader = Type.Object({
  programName: Type.String(),
  advisorName: Type.String(),
  subtitle: Type.String(),
  poweredBy: Type.String(),
});
export type TearsheetHeader = Static<typeof TearsheetHeader>;

export const TearsheetContact = Type.Object({
  name: Type.String(),
  role: Type.String(),
  /** Display line — may be email alone or `email · phone` for partner exports. */
  email: Type.String({ minLength: 1 }),
  phone: Type.Optional(Type.String()),
});
export type TearsheetContact = Static<typeof TearsheetContact>;

export const TearsheetKpi = Type.Object({
  label: Type.String(),
  value: Type.String(),
});
export type TearsheetKpi = Static<typeof TearsheetKpi>;

export const TearsheetCustomBox = Type.Object({
  id: Type.String({
    pattern: '^custom_.+',
    description: 'Must start with custom_',
  }),
  title: Type.String(),
  body: Type.String(),
  span: SectionSpan,
});
export type TearsheetCustomBox = Static<typeof TearsheetCustomBox>;

/** Per-box overrides of the datasource `typography` preset. Unset keys inherit the global value. */
export const TearsheetSectionStyle = Type.Object({
  fontFamily: Type.Optional(TearsheetFontFamily),
  fontSize: Type.Optional(TearsheetFontSize),
  bold: Type.Optional(Type.Boolean()),
  foreground: Type.Optional(Type.String()),
  background: Type.Optional(Type.String()),
  align: Type.Optional(TextAlign),
  minHeight: Type.Optional(Type.Number()),
  barXAxisPosition: Type.Optional(StringEnum(['above', 'below'])),
});
export type TearsheetSectionStyle = Static<typeof TearsheetSectionStyle>;

/** Paper the PDF export uses: one tall page sized to the report, or A4 sheets cut between stencils. */
export const TearsheetPdfPageMode = StringEnum(['continuous', 'a4'], {
  description: 'continuous = one tall Letter page (default); a4 = multi-page A4 that never splits a stencil.',
});
export type TearsheetPdfPageMode = Static<typeof TearsheetPdfPageMode>;

export const TearsheetReportConfig = Type.Object({
  statMetrics: Type.Optional(Type.Array(Type.String())),
  /** Per layout-item performance-statistic picks, in display order (supports duplicated Performance Statistics cards). */
  sectionStatMetrics: Type.Optional(Type.Record(Type.String(), Type.Array(Type.String()))),
  /** Per layout-item date order for tables and charts with a date axis. Unset falls back to the section default. */
  sectionDateSort: Type.Optional(Type.Record(Type.String(), StringEnum(['asc', 'desc']))),
  drawdownTopN: Type.Optional(Type.Integer({ minimum: 1 })),
  drawdownSort: Type.Optional(StringEnum(['depth', 'duration', 'recovery', 'asSent'])),
  sectionDataLimits: Type.Optional(Type.Record(Type.String(), Type.Integer())),
  emptyHistoryMessage: Type.Optional(Type.String()),
  sectionStyles: Type.Optional(Type.Record(Type.String(), TearsheetSectionStyle)),
  sectionTitles: Type.Optional(Type.Record(Type.String(), Type.String())),
  /** Independent text for a duplicated comments / header box. The canonical program description stays on the fund copy. */
  sectionTexts: Type.Optional(
    Type.Record(
      Type.String(),
      Type.Object({
        title: Type.Optional(Type.String({ maxLength: TEARSHEET_LAYOUT_TEXT_MAX_LENGTH })),
        text: Type.Optional(Type.String({ maxLength: TEARSHEET_RICH_TEXT_MAX_LENGTH })),
      }),
    ),
  ),
  /** Column labels to show for a layout item (drawdown table). Absent shows the default columns. */
  sectionColumns: Type.Optional(
    Type.Record(Type.String({ minLength: 1, maxLength: 128 }), Type.Array(Type.String({ minLength: 1, maxLength: 64 }), { maxItems: 8 })),
  ),
  layoutGap: Type.Optional(Type.Number()),
  spacingMode: Type.Optional(StringEnum(['compact', 'comfortable', 'custom'])),
  gapPreference: Type.Optional(StringEnum(['tight', 'balanced', 'fill'])),
  shellPadding: Type.Optional(Type.Number()),
  cardPaddingY: Type.Optional(Type.Number()),
  cardPaddingX: Type.Optional(Type.Number()),
  headerPadding: Type.Optional(Type.Number()),
  kpiGap: Type.Optional(Type.Number()),
  tableCellPadding: Type.Optional(Type.Number()),
  commentaryText: Type.Optional(Type.String()),
  headerFields: Type.Optional(Type.Record(Type.String(), Type.Boolean())),
  /** Key-figure display order. Labels missing from the list keep their live-data order, after the listed ones. */
  headerFieldOrder: Type.Optional(Type.Array(Type.String())),
  /**
   * First month included in monthly returns and in ratio / drawdown calculations.
   * Absent uses the full history. 9AT supplies an already-filtered series, so this is not applied there.
   */
  historyFrom: Type.Optional(YearMonth),
  /** Last month included. Absent uses the report as-of month. Cannot extend past that month. */
  historyTo: Type.Optional(YearMonth),
  autoReflowPanels: Type.Optional(Type.Boolean()),
  /** Builder-only blank rows appended below occupied content (not shown on published reports). */
  builderExtraGridRows: Type.Optional(Type.Integer({ minimum: 0 })),
  /** Absent keeps the historical single tall page; `a4` paginates the print/PDF render. */
  pdfPageMode: Type.Optional(TearsheetPdfPageMode),
});
export type TearsheetReportConfig = Static<typeof TearsheetReportConfig>;

// ─── Live data + render spec (FE owns rendering) ──────────────────────────────

/** HedgeFacts-sourced numbers only — no workflow ids, layout, or document bytes. */
export const TearsheetData = Type.Object(
  {
    asOf: IsoDate,
    cache: Type.Optional(StringEnum(['hit', 'miss'])),
    warning: Type.Optional(Type.String()),
    header: TearsheetHeader,
    commentary: Type.Optional(Type.String()),
    /** Partner snapshot logo (presigned TearsheetAssets URL). Web branding may also use the layout-component logo. */
    logoUrl: Type.Optional(Type.String({ minLength: 1 })),
    monthlyReturns: Type.Optional(Type.Array(MonthlyReturnRow)),
    vami: Type.Optional(Type.Array(VamiPoint)),
    riskSummary: Type.Optional(Type.Array(RiskSummaryRow)),
    sectorAttribution: Type.Optional(Type.Array(SectorAttributionRow)),
    exposureAttribution: Type.Optional(Type.Array(ExposureAttributionRow)),
    topExposure: Type.Optional(Type.Array(InstrumentRow)),
    bottomExposure: Type.Optional(Type.Array(InstrumentRow)),
    topGrossPerformance: Type.Optional(Type.Array(InstrumentRow)),
    bottomGrossPerformance: Type.Optional(Type.Array(InstrumentRow)),
    volatilityContribution: Type.Optional(VolatilityContribution),
    subscriptionInformation: Type.Optional(Type.Array(KeyValueRow)),
    drawdowns: Type.Optional(Type.Array(DrawdownRow)),
    contact: Type.Optional(TearsheetContact),
    disclaimer: Type.Optional(Type.String()),
    kpis: Type.Optional(Type.Array(TearsheetKpi)),
    /** Catalog: Annual Returns table. */
    annualReturns: Type.Optional(Type.Array(AnnualReturnRow)),
    /** Catalog: Rolling Summary (6/12/24/36). */
    rollingSummary: Type.Optional(Type.Array(RollingSummaryRow)),
    /** Catalog: Largest Drawdowns (AmtPct list). */
    largestDrawdowns: Type.Optional(Type.Array(Type.String())),
    /** Catalog: Correlation To Indices. */
    correlationToIndices: Type.Optional(Type.Array(CorrelationRow)),
    /** Catalog charts: Monthly.CummReturn. */
    cumulativeReturn: Type.Optional(Type.Array(TearsheetSeriesPoint)),
    /** Catalog charts: Monthly.PctReturn + DDPct (+ optional VAMI). */
    monthlySeries: Type.Optional(Type.Array(TearsheetSeriesPoint)),
    /** Catalog charts: Rolling.ReturnPct / Std / Sharpe / Std_Annualized. */
    rollingSeries: Type.Optional(Type.Array(TearsheetSeriesPoint)),
    /** Catalog: Init/Maint margin ÷ equity. */
    marginEquitySeries: Type.Optional(Type.Array(TearsheetSeriesPoint)),
    /** Catalog: Sector undiversified VaR (ContributionDetail.VarInd). */
    sectorRiskAllocVar: Type.Optional(Type.Array(NamedValueRow)),
    /** Catalog: Sector P&L attribution (ContributionDetail.PNL_MTD). */
    sectorPnlAttribution: Type.Optional(Type.Array(NamedValueRow)),
    /** Catalog: Component VaR by sector. */
    componentVarBySector: Type.Optional(Type.Array(NamedValueRow)),
    /** Catalog: Performance during N worst index months. */
    performanceDuringIndexWorst: Type.Optional(Type.Array(IndexWorstMonthRow)),
  },
  { $id: 'TearsheetData' },
);
export type TearsheetData = Static<typeof TearsheetData>;
export const tearsheetDataSchema = createSchema(TearsheetData);

export const LiveTearsheetQuery = Type.Object(
  {
    fundId: Type.String({ minLength: 1 }),
    reportDate: IsoDate,
    /** When set (view/PDF), partner path loads the PartnerTearsheets row attached to this report. */
    reportId: Type.Optional(Type.String({ minLength: 1 })),
    /** HedgeFacts book for `fundId`. Omitted means fund (context 1). */
    kind: Type.Optional(Type.Union([Type.Literal('fund'), Type.Literal('managedAccount')])),
    /** `YYYY-MM`. First month of the calculation window. Absent uses full history. */
    historyFrom: Type.Optional(YearMonth),
    /** `YYYY-MM`. Last month of the calculation window. Absent uses `reportDate`. */
    historyTo: Type.Optional(YearMonth),
  },
  { $id: 'LiveTearsheetQuery' },
);
export type LiveTearsheetQuery = Static<typeof LiveTearsheetQuery>;
export const liveTearsheetQuerySchema = createSchema(LiveTearsheetQuery);

// ─── Terms ────────────────────────────────────────────────────────────────────

export const TearsheetTermOption = Type.Object({
  value: IsoDate,
  label: Type.String({ minLength: 1 }),
  frequency: TearsheetTermFrequency,
});
export type TearsheetTermOption = Static<typeof TearsheetTermOption>;

export const TearsheetTermList = Type.Object(
  {
    terms: Type.Array(TearsheetTermOption),
  },
  { $id: 'TearsheetTermList' },
);
export type TearsheetTermList = Static<typeof TearsheetTermList>;
export const tearsheetTermListSchema = createSchema(TearsheetTermList);

export const TearsheetTermQuery = Type.Object(
  {
    fundId: Type.String({ minLength: 1 }),
    frequency: Type.Optional(TearsheetTermFrequency),
    kind: Type.Optional(Type.Union([Type.Literal('fund'), Type.Literal('managedAccount')])),
  },
  { $id: 'TearsheetTermQuery' },
);
export type TearsheetTermQuery = Static<typeof TearsheetTermQuery>;
export const tearsheetTermQuerySchema = createSchema(TearsheetTermQuery);

// ─── Layouts ──────────────────────────────────────────────────────────────────

export const TearsheetLayoutItem = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    colSpan: Type.Union([Type.Literal(3), Type.Literal(6), Type.Literal(9), Type.Literal(12)], {
      description: 'Width on the 4-column builder grid: 1/4, 1/2, 3/4, or full.',
    }),
    rowSpan: Type.Integer({ minimum: 1 }),
    colStart: Type.Optional(
      Type.Integer({
        minimum: 1,
        maximum: 4,
        description: '1-based grid column of the top-left cell. Assigned left-to-right by the builder when absent.',
      }),
    ),
    rowStart: Type.Optional(
      Type.Integer({
        minimum: 1,
        description: '1-based grid row of the top-left cell. Assigned left-to-right by the builder when absent.',
      }),
    ),
  },
  {
    $id: 'TearsheetLayoutItem',
    description: 'One placed section on the 4-column layout grid. Placement is absolute: colStart / rowStart are the top-left cell.',
  },
);
export type TearsheetLayoutItem = Static<typeof TearsheetLayoutItem>;
export const tearsheetLayoutItemSchema = createSchema(TearsheetLayoutItem);

export const TearsheetLayout = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    name: Type.String({ minLength: 1 }),
    skinId: Type.String({
      minLength: 1,
      description: 'Live skin reference applied when this layout renders a tearsheet.',
    }),
    layout: Type.Array(TearsheetLayoutItem),
    customBoxes: Type.Optional(Type.Array(TearsheetCustomBox)),
    reportConfig: Type.Optional(TearsheetReportConfig),
    createdBy: Type.String(),
    clientId: Type.String({ minLength: 1 }),
    isStock: Type.Boolean({
      default: false,
      description: 'Stock layouts render as chrome buttons. Dropdown labels use a (Stock) suffix; other layouts appear only in the custom dropdown.',
    }),
    order: Type.Integer({ minimum: 0, default: 1000, description: 'Display sort order; lower first.' }),
    updatedAt: IsoDateTime,
  },
  { $id: 'TearsheetLayout' },
);
export type TearsheetLayout = Static<typeof TearsheetLayout>;
export const tearsheetLayoutSchema = createSchema(TearsheetLayout);

export const TearsheetLayoutInput = Type.Object(
  {
    name: Type.String({ minLength: 1 }),
    skinId: Type.String({ minLength: 1 }),
    layout: Type.Array(TearsheetLayoutItem),
    customBoxes: Type.Optional(Type.Array(TearsheetCustomBox)),
    reportConfig: Type.Optional(TearsheetReportConfig),
  },
  { $id: 'TearsheetLayoutInput' },
);
export type TearsheetLayoutInput = Static<typeof TearsheetLayoutInput>;
export const tearsheetLayoutInputSchema = createSchema(TearsheetLayoutInput);

export const SHOW_FAVORITE_LAYOUT_COUNT = 3;

/** Built-in layout template ids merged by the BFF; not stored in DynamoDB. */
export const TEARSHEET_STOCK_LAYOUT_IDS = ['executive', 'institutional', 'modern'] as const;
export type TearsheetStockLayoutId = (typeof TEARSHEET_STOCK_LAYOUT_IDS)[number];

export const TearsheetLayoutList = Type.Object(
  {
    layouts: Type.Array(TearsheetLayout),
    favoriteLayoutIds: Type.Array(Type.String(), {
      maxItems: SHOW_FAVORITE_LAYOUT_COUNT,
      description: 'Tenant-wide favorite layout ids for the Create page quick picks.',
    }),
    nextCursor: Type.Optional(Type.String()),
  },
  { $id: 'TearsheetLayoutList' },
);
export type TearsheetLayoutList = Static<typeof TearsheetLayoutList>;
export const tearsheetLayoutListSchema = createSchema(TearsheetLayoutList);

export const TearsheetLayoutFavoritesInput = Type.Object(
  {
    favoriteLayoutIds: Type.Array(Type.String(), {
      maxItems: SHOW_FAVORITE_LAYOUT_COUNT,
      description: 'Ordered tenant-wide favorites. Empty strings keep an empty slot.',
    }),
  },
  { $id: 'TearsheetLayoutFavoritesInput' },
);
export type TearsheetLayoutFavoritesInput = Static<typeof TearsheetLayoutFavoritesInput>;
export const tearsheetLayoutFavoritesInputSchema = createSchema(TearsheetLayoutFavoritesInput);

export const SHOW_ON_SCREEN_SKIN_COUNT = 5;

export const TearsheetDatasourceConfiguration = Type.Object(
  {
    datasource: Type.String({ minLength: 1 }),
    brandingPresets: TearsheetBrandingPresets,
    customFields: TearsheetCustomFields,
    favoriteLayoutIds: Type.Array(Type.String(), {
      maxItems: SHOW_FAVORITE_LAYOUT_COUNT,
      description: 'Toolbar layout quick picks (stock + saved layout ids).',
    }),
    favoriteSkinIds: Type.Array(Type.String(), {
      maxItems: SHOW_ON_SCREEN_SKIN_COUNT,
      description: 'Toolbar skin quick picks (stock + custom skin ids).',
    }),
    updatedBy: Type.Optional(Type.String({ minLength: 1 })),
    updatedAt: IsoDateTime,
  },
  { $id: 'TearsheetDatasourceConfiguration' },
);
export type TearsheetDatasourceConfiguration = Static<typeof TearsheetDatasourceConfiguration>;
export const tearsheetDatasourceConfigurationSchema = createSchema(TearsheetDatasourceConfiguration);

export const TearsheetSkinVisibility = StringEnum(['private', 'public'], {
  description: 'private = owner only; public = every user in the same datasource.',
});
export type TearsheetSkinVisibility = Static<typeof TearsheetSkinVisibility>;

export const TearsheetSkinOrigin = StringEnum(['stock', 'own', 'public'], {
  description: 'How the skin appears in the caller’s catalog.',
});
export type TearsheetSkinOrigin = Static<typeof TearsheetSkinOrigin>;

export const TearsheetSkinColors = Type.Object(
  {
    name: Type.String({ minLength: 1 }),
    bg: Type.String({ minLength: 1 }),
    panel: Type.String({ minLength: 1 }),
    panel2: Type.String({ minLength: 1 }),
    text: Type.String({ minLength: 1 }),
    muted: Type.String({ minLength: 1 }),
    accent: Type.String({ minLength: 1 }),
    border: Type.String({ minLength: 1 }),
  },
  { $id: 'TearsheetSkinColors' },
);
export type TearsheetSkinColors = Static<typeof TearsheetSkinColors>;
export const tearsheetSkinColorsSchema = createSchema(TearsheetSkinColors);

export const TearsheetSkinMeta = Type.Object(
  {
    origin: TearsheetSkinOrigin,
    visibility: TearsheetSkinVisibility,
  },
  { $id: 'TearsheetSkinMeta' },
);
export type TearsheetSkinMeta = Static<typeof TearsheetSkinMeta>;

export const TearsheetSkinInput = Type.Object(
  {
    colors: TearsheetSkinColors,
    visibility: TearsheetSkinVisibility,
  },
  { $id: 'TearsheetSkinInput' },
);
export type TearsheetSkinInput = Static<typeof TearsheetSkinInput>;
export const tearsheetSkinInputSchema = createSchema(TearsheetSkinInput);

export const TearsheetSkinPinsInput = Type.Object(
  {
    showInList: Type.Array(Type.String(), {
      maxItems: SHOW_ON_SCREEN_SKIN_COUNT,
      description: 'Tenant-wide pinned skin ids in toolbar order. Empty strings keep an empty slot.',
    }),
  },
  { $id: 'TearsheetSkinPinsInput' },
);
export type TearsheetSkinPinsInput = Static<typeof TearsheetSkinPinsInput>;
export const tearsheetSkinPinsInputSchema = createSchema(TearsheetSkinPinsInput);

export const TearsheetSkinList = Type.Object(
  {
    showInList: Type.Array(Type.String(), {
      maxItems: SHOW_ON_SCREEN_SKIN_COUNT,
      description: 'Up to 5 toolbar slots. Empty strings are holes; trailing holes may be omitted.',
    }),
    allSkins: Type.Record(Type.String({ minLength: 1 }), TearsheetSkinColors, {
      description: 'Full catalog keyed by skin id.',
    }),
    meta: Type.Optional(
      Type.Record(Type.String({ minLength: 1 }), TearsheetSkinMeta, {
        description: 'Origin and visibility per skin id.',
      }),
    ),
  },
  { $id: 'TearsheetSkinList' },
);
export type TearsheetSkinList = Static<typeof TearsheetSkinList>;
export const tearsheetSkinListSchema = createSchema(TearsheetSkinList);

// ─── Reports (saved drafts / workflow) ────────────────────────────────────────

/**
 * Allowed next statuses from each workflow state.
 * Reject = submitted/approved → archived. Unpublish = published → draft; re-open = archived → draft.
 */
export const TEARSHEET_REPORT_STATUS_TRANSITIONS: Record<TearsheetReportStatus, readonly TearsheetReportStatus[]> = {
  draft: ['submitted'],
  submitted: ['approved', 'archived'],
  approved: ['published', 'archived'],
  published: ['draft'],
  archived: ['draft'],
};

/** Saved tearsheet for a fund + as-of month within a datasource (layout implies skin). */
export const TearsheetReport = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    fundId: Type.String({ minLength: 1, description: 'Hashed fund id (`h1.…`).' }),
    asOf: YearMonth,
    layoutId: Type.String({ minLength: 1 }),
    status: TearsheetReportStatus,
    /** Hashed HedgeFacts user id (`h1.…`) of the author. */
    createdBy: Type.String({ minLength: 1 }),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    readOnly: Type.Optional(Type.Boolean()),
  },
  { $id: 'TearsheetReport' },
);
export type TearsheetReport = Static<typeof TearsheetReport>;
export const tearsheetReportSchema = createSchema(TearsheetReport);

/** Report row for the fund dashboard — includes display names resolved by the BFF. */
export const TearsheetReportListItem = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    fundId: Type.String({ minLength: 1, description: 'Hashed fund id (`h1.…`).' }),
    asOf: YearMonth,
    layoutId: Type.String({ minLength: 1 }),
    layoutName: Type.String({ minLength: 1 }),
    status: TearsheetReportStatus,
    createdBy: Type.String({ minLength: 1 }),
    createdByName: Type.String({ minLength: 1 }),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    readOnly: Type.Optional(Type.Boolean()),
  },
  { $id: 'TearsheetReportListItem' },
);
export type TearsheetReportListItem = Static<typeof TearsheetReportListItem>;
export const tearsheetReportListItemSchema = createSchema(TearsheetReportListItem);

export const TearsheetReportInput = Type.Object(
  {
    fundId: Type.String({ minLength: 1 }),
    asOf: YearMonth,
    layoutId: Type.String({ minLength: 1 }),
  },
  {
    $id: 'TearsheetReportInput',
    description: 'Create a draft tearsheet. Skin comes from the layout. Duplicate fundId + asOf returns 409.',
  },
);
export type TearsheetReportInput = Static<typeof TearsheetReportInput>;
export const tearsheetReportInputSchema = createSchema(TearsheetReportInput);

/** PUT body: either a status transition or (draft-only) asOf/layoutId update. */
export const TearsheetReportUpdate = Type.Object(
  {
    status: Type.Optional(TearsheetReportStatus),
    asOf: Type.Optional(YearMonth),
    layoutId: Type.Optional(Type.String({ minLength: 1 })),
  },
  {
    $id: 'TearsheetReportUpdate',
    description:
      'Status transition (status required) or draft field update (asOf/layoutId). Fund is immutable. Do not mix status and field updates in one request.',
  },
);
export type TearsheetReportUpdate = Static<typeof TearsheetReportUpdate>;
export const tearsheetReportUpdateSchema = createSchema(TearsheetReportUpdate);

export const TearsheetReportIdParam = Type.Object(
  {
    reportId: Type.String({ minLength: 1 }),
  },
  { $id: 'TearsheetReportIdParam' },
);
export type TearsheetReportIdParam = Static<typeof TearsheetReportIdParam>;
export const tearsheetReportIdParamSchema = createSchema(TearsheetReportIdParam);

export const TearsheetReportListQuery = Type.Object(
  {
    fundId: Type.String({ minLength: 1 }),
  },
  { $id: 'TearsheetReportListQuery' },
);
export type TearsheetReportListQuery = Static<typeof TearsheetReportListQuery>;
export const tearsheetReportListQuerySchema = createSchema(TearsheetReportListQuery);

export const TearsheetReportList = Type.Object(
  {
    reports: Type.Array(TearsheetReportListItem),
  },
  { $id: 'TearsheetReportList' },
);
export type TearsheetReportList = Static<typeof TearsheetReportList>;
export const tearsheetReportListSchema = createSchema(TearsheetReportList);

/** PDF export job result. `url` is present only when `status` is `successful`. */
export const TearsheetReportPdfStatus = StringEnum(['pending', 'error', 'successful'], {
  $id: 'TearsheetReportPdfStatus',
});
export type TearsheetReportPdfStatus = Static<typeof TearsheetReportPdfStatus>;

export const TearsheetReportPdf = Type.Object(
  {
    status: TearsheetReportPdfStatus,
    url: Type.Optional(Type.String({ minLength: 1, description: 'Signed S3 GET URL when status is successful.' })),
    detail: Type.Optional(Type.String({ description: 'Human-readable detail when status is error.' })),
  },
  { $id: 'TearsheetReportPdf' },
);
export type TearsheetReportPdf = Static<typeof TearsheetReportPdf>;
export const tearsheetReportPdfSchema = createSchema(TearsheetReportPdf);

/** Optional paper mode for GET .../pdf — chooses continuous Letter vs paginated A4 for this export. */
export const TearsheetReportPdfQuery = Type.Object(
  {
    pdfPageMode: Type.Optional(TearsheetPdfPageMode),
    regenerate: Type.Optional(
      Type.String({
        description:
          'Pass `true` on the first poll to ignore the stored PDF and render again after a template or branding change. Omit on later polls.',
      }),
    ),
  },
  { $id: 'TearsheetReportPdfQuery' },
);
export type TearsheetReportPdfQuery = Static<typeof TearsheetReportPdfQuery>;
export const tearsheetReportPdfQuerySchema = createSchema(TearsheetReportPdfQuery);

// ─── Funds (SOAP getFundsList) ────────────────────────────────────────────────

export const FundSummary = Type.Object(
  {
    fundId: Type.String({ minLength: 1 }),
    fundName: Type.String({ minLength: 1 }),
    readOnly: Type.Optional(Type.Boolean()),
    /** Absent on partner rows. HedgeFacts rows say which SOAP context the id uses. */
    kind: Type.Optional(Type.Union([Type.Literal('fund'), Type.Literal('managedAccount')])),
  },
  { $id: 'FundSummary' },
);
export type FundSummary = Static<typeof FundSummary>;
export const fundSummarySchema = createSchema(FundSummary);

export const FundList = Type.Object(
  {
    funds: Type.Array(FundSummary),
    /** HedgeFacts Config HedgeFunds. Present on hedgefacts sessions. */
    fundsEnabled: Type.Optional(Type.Boolean()),
    /** HedgeFacts Config ManagedAccounts. */
    managedAccountsEnabled: Type.Optional(Type.Boolean()),
    /** Which rows `funds` contains. */
    book: Type.Optional(Type.Union([Type.Literal('fund'), Type.Literal('managedAccount'), Type.Literal('both')])),
  },
  { $id: 'FundList' },
);
export type FundList = Static<typeof FundList>;
export const fundListSchema = createSchema(FundList);
